import { ReactNode, useState } from "react";
import dayjs from "dayjs";
import { PrimaryButton, TextInput } from "@normasteaching/norma-ui";
import { Modal } from "../../UI/Modal/Modal.tsx";
import { useUpsertWithdrawalRequest } from "../../SpeakingClassService/Withdrawal/useUpsertWithdrawalRequest.ts";
import {
  WithdrawalRequestDTO,
  WithdrawalRequestStatus,
} from "../../SpeakingClassService/Withdrawal/WithdrawalRequestDTO.ts";
import {
  WITHDRAWAL_PLAN_TYPE_LABELS,
  WITHDRAWAL_RECIPIENT_LABELS,
  WITHDRAWAL_STATUS_LABELS,
} from "./withdrawalLabels.ts";
import { useWithdrawalOrderSummary } from "./useWithdrawalOrderSummary.ts";
import { usePurchasedItemNames } from "../../ProductService/PurchasedItem/usePurchasedItemNames.ts";
import { personNameFromEmail } from "./personNameFromEmail.ts";
import { extractDealId } from "./extractDealId.ts";
import {
  describeWithdrawalWindow,
  isRequestedWithinWithdrawalWindow,
  STUDENTS_TEAM_EMAIL,
} from "./withdrawalWindow.ts";

type Props = {
  request: WithdrawalRequestDTO;
  onClose: () => void;
  onUpdated: () => void;
};

const PROCESSING_WINDOW_IN_DAYS = 14;
const HUBSPOT_DEAL_BASE_URL =
  "https://app-eu1.hubspot.com/contacts/147951100/record/0-3";

function formatDate(date: string): string {
  return dayjs(date).format("DD/MM/YYYY");
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="font-medium text-gray-900 text-right break-all">
        {value}
      </span>
    </div>
  );
}

function DealRow({ dealId }: { dealId?: string }) {
  if (!dealId) return <Row label="Deal HubSpot" value="Non trovato" />;

  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-gray-500 shrink-0">Deal HubSpot</span>
      <a
        href={`${HUBSPOT_DEAL_BASE_URL}/${dealId}`}
        target="_blank"
        rel="noreferrer"
        className="font-medium text-blue-600 hover:underline text-right break-all"
      >
        {dealId}
      </a>
    </div>
  );
}

function PersonRow({
  label,
  name,
  email,
  emptyLabel,
}: {
  label: string;
  name?: string;
  email?: string;
  emptyLabel: string;
}) {
  const displayName = name ?? personNameFromEmail(email);

  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="text-right">
        <span className="font-medium text-gray-900 block">
          {displayName ?? emptyLabel}
        </span>
        {email && (
          <span className="text-gray-500 text-xs block break-all">{email}</span>
        )}
      </span>
    </div>
  );
}

function Section({
  title,
  muted,
  children,
}: {
  title: string;
  muted?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={`rounded-lg p-4 space-y-1 border ${
        muted
          ? "border-dashed border-gray-300 bg-gray-50 text-gray-500"
          : "border-gray-200"
      }`}
    >
      <p
        className={`text-sm font-semibold mb-2 ${muted ? "text-gray-500" : "text-gray-700"}`}
      >
        {muted ? `${title} (bozza)` : title}
      </p>
      {children}
    </div>
  );
}

function DraftNotice() {
  return (
    <p className="text-xs text-gray-500 italic pt-1">
      Anteprima non confermata — salva per applicare la modifica.
    </p>
  );
}

function OrderSection({
  orderId,
  isDraft,
}: {
  orderId: string;
  isDraft: boolean;
}) {
  const { order, gatewayOrderId, isLoading } = useWithdrawalOrderSummary(
    orderId || undefined,
  );
  const { resolveName } = usePurchasedItemNames();

  if (!orderId) {
    return (
      <Section title="Ordine" muted={isDraft}>
        <p className="text-sm text-gray-500">
          Nessun ordine associato a questo acquisto.
        </p>
      </Section>
    );
  }

  if (isLoading) {
    return (
      <Section title="Ordine" muted={isDraft}>
        <p className="text-sm text-gray-500">Carico l&apos;ordine…</p>
      </Section>
    );
  }

  if (!order) {
    return (
      <Section title="Ordine" muted={isDraft}>
        <Row label="ID ordine" value={gatewayOrderId ?? "—"} />
        <p className="text-sm text-gray-500 pt-1">
          Ordine non trovato tra Stripe e HeyLight.
        </p>
      </Section>
    );
  }

  const productName = resolveName({
    id: order.productId,
    type: order.productType,
    snapshot: order.productSnapshot,
  }).name;

  return (
    <Section title="Ordine" muted={isDraft}>
      <Row label="Prodotto" value={productName} />
      <Row label="Gateway" value={order.gateway} />
      <Row
        label="Importo"
        value={`€${(order.amountInCent.amount / 100).toFixed(2)}`}
      />
      <Row label="Stato" value={order.status} />
      <Row label="Data ordine" value={formatDate(order.createdAt)} />
      <a
        href={order.detailPath}
        target="_blank"
        rel="noreferrer"
        className="block text-sm text-blue-600 hover:underline pt-1"
      >
        Apri l&apos;ordine →
      </a>
      {isDraft && <DraftNotice />}
    </Section>
  );
}

export function WithdrawalRequestDetailsModal({
  request,
  onClose,
  onUpdated,
}: Props) {
  const [status, setStatus] = useState(request.status);
  const [requestedAt, setRequestedAt] = useState(
    dayjs(request.requestedAtUtc).format("YYYY-MM-DD"),
  );
  const [notes, setNotes] = useState(request.notes ?? "");
  const [orderId, setOrderId] = useState(request.orderId ?? "");
  const [dealId, setDealId] = useState(request.dealId ?? "");
  const [notifyCloser, setNotifyCloser] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { updateRequest, resyncWithHubSpot, loading } =
    useUpsertWithdrawalRequest();

  const processingDeadline = dayjs(requestedAt)
    .add(PROCESSING_WINDOW_IN_DAYS, "day")
    .format("DD/MM/YYYY");

  const isWithinWithdrawalWindow = isRequestedWithinWithdrawalWindow(
    new Date(request.requestedAtUtc),
    request.withdrawalDeadlineUtc,
  );

  const handleSave = async () => {
    setError(null);
    try {
      await updateRequest(request.id, {
        status,
        requestedAtUtc: new Date(requestedAt),
        notes,
        orderId,
        dealId,
      });
      onUpdated();
    } catch {
      setError("Errore durante il salvataggio. Riprova.");
    }
  };

  const handleResync = async () => {
    setError(null);
    try {
      if (dealId !== (request.dealId ?? "")) {
        await updateRequest(request.id, { dealId });
      }
      await resyncWithHubSpot(request.id, notifyCloser);
      onUpdated();
    } catch {
      setError(
        "Errore durante la sincronizzazione con HubSpot. Verifica l'ID del deal e riprova.",
      );
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Richiesta di recesso" size="lg">
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <Section title="Cliente">
            <Row
              label="Nome"
              value={`${request.userGivenName} ${request.userFamilyName}`}
            />
            <Row label="Email" value={request.userEmail} />
            <Row
              label="Piano"
              value={
                request.plans.length > 0
                  ? request.plans
                      .map((plan) => WITHDRAWAL_PLAN_TYPE_LABELS[plan.planType])
                      .join(" + ")
                  : "—"
              }
            />
            <Row
              label="Lezioni fruite alla richiesta"
              value={String(request.attendedLessonsCount)}
            />
          </Section>

          <OrderSection
            orderId={orderId}
            isDraft={orderId !== (request.orderId ?? "")}
          />

          <Section title="Date">
            <Row
              label="Data acquisto"
              value={formatDate(request.purchasedAtUtc)}
            />
            <Row
              label="Termine recesso"
              value={formatDate(request.withdrawalDeadlineUtc)}
            />
            <Row
              label="Data richiesta"
              value={formatDate(request.requestedAtUtc)}
            />
            <Row
              label="Tipologia"
              value={describeWithdrawalWindow(isWithinWithdrawalWindow)}
            />
            <Row label="Da processare entro" value={processingDeadline} />
            {request.refundedAtUtc && (
              <Row
                label="Rimborsata il"
                value={formatDate(request.refundedAtUtc)}
              />
            )}
          </Section>

          <Section
            title="Deal e closer"
            muted={dealId !== (request.dealId ?? "")}
          >
            {dealId !== (request.dealId ?? "") && (
              <>
                <DealRow dealId={dealId || undefined} />
                <p className="text-xs text-gray-500 pt-1">
                  Apri il deal per verificare owner e team prima di salvare.
                </p>
                <DraftNotice />
                <div className="border-t border-gray-200 my-2" />
                <p className="text-xs text-gray-400">Valori attuali</p>
              </>
            )}
            {request.dealId || request.closerEmail ? (
              <>
                <DealRow dealId={request.dealId} />
                <PersonRow
                  label="Closer"
                  name={request.closerName}
                  email={request.closerEmail}
                  emptyLabel="Non trovato"
                />
                <Row
                  label="Team"
                  value={request.closerTeamName ?? "Non individuato"}
                />
                <PersonRow
                  label="Team leader"
                  email={request.teamLeaderEmail}
                  emptyLabel="—"
                />
              </>
            ) : (
              <p className="text-sm text-gray-500">
                Nessun deal associato alla richiesta.
              </p>
            )}
          </Section>

          <Section title="Notifica interna">
            {request.notificationRecipient ? (
              <>
                <Row
                  label="Destinatario"
                  value={
                    WITHDRAWAL_RECIPIENT_LABELS[request.notificationRecipient]
                  }
                />
                <Row
                  label="Inviata il"
                  value={
                    request.notifiedAtUtc
                      ? formatDate(request.notifiedAtUtc)
                      : "—"
                  }
                />
              </>
            ) : (
              <p className="text-sm text-gray-500">
                Nessuna notifica inviata per questa richiesta.
              </p>
            )}
          </Section>

          {request.plans.map((plan) => (
            <Section
              key={plan.planId}
              title={`Piano ${WITHDRAWAL_PLAN_TYPE_LABELS[plan.planType]}`}
            >
              {plan.totalLessons != null && (
                <Row label="Lezioni totali" value={String(plan.totalLessons)} />
              )}
              {plan.lessonsPerWeek != null && (
                <Row
                  label="Lezioni a settimana"
                  value={String(plan.lessonsPerWeek)}
                />
              )}
              {plan.durationInWeeks != null && (
                <Row
                  label="Durata (settimane)"
                  value={String(plan.durationInWeeks)}
                />
              )}
              {plan.startedAtWeekUtc && plan.endedAtWeekUtc && (
                <Row
                  label="Periodo"
                  value={`${formatDate(plan.startedAtWeekUtc)} → ${formatDate(plan.endedAtWeekUtc)}`}
                />
              )}
            </Section>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Stato</label>
            <select
              value={status}
              onChange={(e) =>
                setStatus(e.target.value as WithdrawalRequestStatus)
              }
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              {Object.entries(WITHDRAWAL_STATUS_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              Data della richiesta
            </label>
            <TextInput
              type="date"
              value={requestedAt}
              onChange={(e) => setRequestedAt(e.target.value)}
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              ID ordine
            </label>
            <TextInput
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="Es. 6aa00d3b22ab5d156251933b"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              ID deal HubSpot
            </label>
            <TextInput
              value={dealId}
              onChange={(e) => setDealId(extractDealId(e.target.value))}
              placeholder="ID o link del deal"
            />
            <label className="flex items-center gap-2 text-xs text-gray-600 mt-1">
              <input
                type="checkbox"
                checked={notifyCloser}
                onChange={(e) => setNotifyCloser(e.target.checked)}
              />
              {isWithinWithdrawalWindow
                ? "Invia le email a closer e team leader"
                : `Invia l'email a ${STUDENTS_TEAM_EMAIL}`}
            </label>
            <button
              onClick={handleResync}
              disabled={loading || !dealId}
              className="mt-1 px-3 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            >
              Sincronizza con HubSpot
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">Note</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full min-h-[120px] p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Annotazioni interne sulla richiesta di recesso"
          />
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
            {error}
          </p>
        )}

        <div className="flex gap-3 justify-end pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-50"
          >
            Chiudi
          </button>
          <PrimaryButton
            label={loading ? "Salvataggio..." : "Salva"}
            onClick={handleSave}
            disabled={loading}
          />
        </div>
      </div>
    </Modal>
  );
}
